import {
  AttachmentListRespType,
  AttachmentRespType,
  AttachmentType,
} from './caseReport'

/**
 * Finds existing AttachmentList from the 'lists' array.
 * @param lists input array of AttachmentLists
 * @param type the attachmentType to look for
 * @returns an AttachmentList of given type or 'undefined'
 */
export function findInAttachmentLists(
  lists: AttachmentListRespType[],
  type: AttachmentType
): AttachmentListRespType | undefined {
  return lists.find((attLst) => attLst.attachmentType === type)
}

/**
 * Finds AttachmentList of given type and returns its array of attachments
 * or creates a new empty array of AttachmentRespType.
 * @param attachmentList input array of AttachmentLists
 * @param type the attachmentType to look for
 * @returns AttachmentRespType[]
 */
export function getAttachmentsFromAttachmentListOrEmpty(
  attachmentList: AttachmentListRespType[] | undefined,
  type: AttachmentType
): AttachmentRespType[] {
  let attLstOrUndef: AttachmentListRespType | undefined
  if (attachmentList == null) {
    // get over possibly undefined attachmentList[]
    attLstOrUndef = undefined
  } else {
    attLstOrUndef = findInAttachmentLists(attachmentList, type)
  }
  if (attLstOrUndef == null || attLstOrUndef.attachments == null) {
    // Existing AttachmentList not found;
    // returning new empty array
    return []
  }
  // Found existing AttachmentList
  // return it
  return attLstOrUndef.attachments
}

/**
 * Gets an existing AttachmentList from the 'lists' array
 * or creates a new empty AttachmentList of given type.
 *
 * Note: this function is currently not used in the Forms.
 *
 * @param lists input array of AttachmentLists
 * @param type the attachmentType to look for
 * @returns an AttachmentList
 */
export function getFromAttachmentListsOrNew(
  lists: AttachmentListRespType[] | undefined,
  type: AttachmentType
): AttachmentListRespType {
  let attLstOrUndef: AttachmentListRespType | undefined
  if (lists == null) {
    // get over possibly undefined attachmentList[]
    attLstOrUndef = undefined
  } else {
    attLstOrUndef = findInAttachmentLists(lists, type)
  }
  if (attLstOrUndef == null) {
    // Existing AttachmentList not found;
    // returning new empty one
    return {
      attachmentType: type,
      attachments: [],
    }
  }
  // Found existing AttachmentList
  // return it
  return attLstOrUndef
}

/**
 * Returns array of AttachmentLists so that it contains all edited AttachmentsList
 * and original AttachmentLists of AttachmentTypes that were not edited.
 * @param original
 * @param edited
 * @returns
 */
export function combineAttachmentsList(
  original: AttachmentListRespType[] | undefined,
  edited: AttachmentListRespType[]
): AttachmentListRespType[] {
  if (original == null || original.length === 0) {
    // If there is no original array of AttachmentLists,
    // just return the new edited array
    return edited
  }
  // Combine original with edited array of AttachmentLists
  const editedAttachmentTypes: (string | null)[] = edited.map(
    (attLst) => attLst.attachmentType
  )
  const originalWithoutEdited: AttachmentListRespType[] = original.filter(
    (attLst) => !editedAttachmentTypes.includes(attLst.attachmentType)
  )
  return [...originalWithoutEdited, ...edited]
}

/**
 * Creates a new AttachmentList of given type with given attachments
 * @param attachmentList input array of AttachmentLists
 * @param type the attachmentType to look for
 * @returns AttachmentRespType[]
 */
export function createAttachmentList(
  attachmentType: AttachmentType,
  attachments?: AttachmentRespType[]
): AttachmentListRespType {
  return {
    attachmentType: attachmentType,
    attachments: attachments,
  }
}
